import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import * as XLSX from 'xlsx';
import { UnpaidPaymentsAdapterService, UnifiedProductOption, UnifiedUnpaidPayment } from '../../unpaid-payments-adapter.service';
import { ProductConfigService } from '../../../../../core/services/product-config.service';
import { UnpaidPartnerTileSummaryService } from '../../unpaid-partner-tile-summary.service';
import { TableHeader } from '../../../../../shared/components/tables/paginated-table/paginated-table.component';
import { UnpaidPaymentsCalculationService } from '../../unpaid-payments-calculation.service';
import { DropdownOption } from '../../../../../shared/components/ui/dropdown/dropdown.component';
import { UnpaidPaymentsModalService } from '../../unpaid-payments-modal.service';
import {
  FmListRouteDefaults,
  FmListRouteState,
  buildFmListQueryParams,
  getDefaultIsDummy,
  parseFmListRouteState,
} from '../../unpaid-payments-route-query.util';
import { getDefaultFmFilter } from '../../unpaid-payments-hub-products.config';
import {
  UnpaidPaymentsFundingSourceId,
  UnpaidPaymentsFundingSourceOption,
} from '../../unpaid-payments-modal.types';
import { UNPAID_PAYMENTS_BULK_SELECTION_STYLES } from '../../unpaid-payments-bulk-selection.styles';
import { UnpaidPaymentsIsDummyService } from '../../unpaid-payments-is-dummy.service';
import { UnpaidPaymentsAccessService } from '../../unpaid-payments-access.service';
import { UnpaidPaymentsHubOverviewService } from '../../unpaid-payments-hub-overview.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IconComponentV2 } from '../../../../../shared/components/icon-component/icon.component';
import { PaginatedTableComponent } from '../../../../../shared/components/tables/paginated-table/paginated-table.component';
import {
  DropdownComponent,
  DropdownTriggerDirective,
} from '../../../../../shared/components/ui/dropdown/dropdown.component';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { ToggleComponent } from '../../../../../shared/components/ui/toggle/toggle.component';
import { SelectionComponent } from '../../../../../shared/components/ui/selection/selection.component';
import { UnpaidPartnerOverviewCardsComponent } from '../../components/unpaid-partner-overview-cards.component';
import { UnpaidPaymentsIsDummyToggleComponent } from '../../components/unpaid-payments-is-dummy-toggle/unpaid-payments-is-dummy-toggle.component';
@Component({
  selector: 'app-unpaid-payments-recuvery',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IconComponentV2,
    PaginatedTableComponent,
    DropdownComponent,
    DropdownTriggerDirective,
    ButtonComponent,
    ToggleComponent,
    SelectionComponent,
    UnpaidPartnerOverviewCardsComponent,
    UnpaidPaymentsIsDummyToggleComponent,
  ],
  templateUrl: './unpaid-payments-recuvery.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [UNPAID_PAYMENTS_BULK_SELECTION_STYLES],
})
export class UnpaidPaymentsRecuveryComponent implements OnInit {
  private readonly calculationService = inject(UnpaidPaymentsCalculationService);

  readonly supportedFilters = [4, 5, 8, 9, 10, 11, 12, 13] as const;
  readonly productKey = 'recuvery';
  readonly defaultStartDate = '2025-01-01';
  readonly defaultEndDate = this.calculationService.unpaidPaymentsTodayYmd();
  readonly loading = signal(false);
  readonly paying = signal(false);
  readonly exporting = signal(false);
  readonly markingUrgent = signal(false);
  readonly tableBusy = computed(() => this.loading() || this.paying() || this.markingUrgent());
  readonly tableBusyMessage = computed(() => {
    if (this.paying()) return 'Processing payments...';
    if (this.markingUrgent()) return 'Processing...';
    return 'Loading unpaid records...';
  });
  readonly payAllButtonLabel = computed(() =>
    this.calculationService.resolveUnpaidPayAllLabel({
      fmFilter: this.fmFilter(),
      fmStatusFilter: this.fmStatusFilter(),
    })
  );
  readonly urgentNotes = signal<any[]>([]);
  readonly payments = signal<UnifiedUnpaidPayment[]>([]);
  readonly search = signal('');
  readonly searchApplied = signal(false);
  readonly startDate = signal(this.defaultStartDate);
  readonly endDate = signal(this.defaultEndDate);
  readonly endDateMax = computed(() => this.calculationService.unpaidPaymentsTodayYmd());
  readonly startDateMax = computed(() =>
    this.calculationService.resolveListStartDateMax(this.endDate(), this.endDateMax())
  );
  readonly fmFilter = signal<number>(getDefaultFmFilter('recuvery'));
  readonly fmStatusFilter = signal('0');
  readonly vipType = signal<number>(0);
  readonly urgentOnly = signal(false);
  readonly availableSources = signal<{ label: string; value: string }[]>([]);
  readonly selectedIds = signal<Array<number | string>>([]);
  readonly page = signal(1);
  readonly pageSize = 10;
  readonly totalCount = signal(0);
  readonly totalPages = signal(0);
  readonly totalAmount = signal(0);
  readonly payableCount = signal(0);
  readonly overviewCalculating = signal(false);
  readonly listAggregateIds = signal<unknown>(null);
  private payableCountRequestId = 0;
  readonly offset = computed(() => (this.page() - 1) * this.pageSize);
  readonly canGoPrev = computed(() => this.page() > 1);
  readonly canGoNext = computed(() => this.page() < this.totalPages());
  private readonly paymentConfirmTitle =
    'Are you sure you want to pay the Business for this unpaid payment ?';
  private readonly availableSourceBalance = signal<Record<string, number>>({});
  readonly overview = computed(() => this.partnerTileSummary.overviewFor(this.productKey));
  readonly showUrgentOverviewAction = true;
  readonly selectedRows = computed(() =>
    this.payments().filter((p) => {
      const id = this.paymentId(p);
      return id !== null && this.selectedIds().includes(id);
    })
  );
  readonly totalSelectedAmount = computed(() =>
    this.selectedPayablePayments().reduce((sum, p) => sum + this.getPayableAmount(p), 0)
  );
  readonly selectedPayablePayments = computed(() =>
    this.selectedRows().filter((p) => this.isRowPayable(p))
  );
  readonly selectedPayableCount = computed(() => this.selectedPayablePayments().length);
  readonly selectedBusinessesCount = computed(() => {
    const ids = new Set<string>();
    for (const row of this.selectedPayablePayments()) {
      const bizId = row?.['doctor_id'] ?? row?.['partner_id'];
      if (bizId !== undefined && bizId !== null && bizId !== '') {
        ids.add(String(bizId));
      }
    }
    return ids.size;
  });
  readonly selectedUrgentCount = computed(() =>
    this.selectedPayablePayments().filter((row) => row['is_urgent'] == 1).length
  );
  readonly selectedUrgentAmount = computed(() =>
    this.selectedPayablePayments()
      .filter((row) => row['is_urgent'] == 1)
      .reduce((sum, row) => sum + this.getPayableAmount(row), 0)
  );
  readonly fmFilterDropdownValue = computed(() => String(this.fmFilter()));
  readonly vipTypeDropdownValue = computed(() => String(this.vipType()));
  readonly canSelectRowBind = (row: UnifiedUnpaidPayment) => this.canSelectRow(row);
  readonly filterDropdownOptions: DropdownOption[] = [
    // { value: '4', label: 'Won Disputes' }, // commenting these are these are yet to be implemented at product's end
    { value: '5', label: 'ACH Businesses' },
    { value: '8', label: 'Transfer Disabled' },
    // { value: '9', label: 'Cash Adjustment' }, // commenting these are these are yet to be implemented at product's end
    { value: '10', label: 'Bank Not Added - Transfer Disabled' },
    { value: '11', label: 'Global Payouts - Lendee Supporter' },
    { value: '12', label: 'Global Payouts - Credee' },
    { value: '13', label: 'Global Payouts - Denefits' },
  ];
  readonly vipDropdownOptions: DropdownOption[] = [
    { value: '0', label: 'All' },
    { value: '1', label: 'VIP' },
  ];
  readonly exportDropdownOptions: DropdownOption[] = [
    { value: 'xlsx', label: 'Export XLSX', icon: 'fileText' },
    { value: 'csv', label: 'Export CSV', icon: 'fileText' },
  ];
  readonly openActionMenuRowId = signal<string | null>(null);
  readonly openActionMenuRow = signal<UnifiedUnpaidPayment | null>(null);
  readonly actionMenuPosition = signal<{ top: number; left: number } | null>(null);

  @ViewChild('actionMenuPanel') private actionMenuPanel?: ElementRef<HTMLElement>;
  private actionMenuTrigger: HTMLElement | null = null;
  private readonly actionMenuWidth = 220;
  private readonly actionMenuGap = 4;
  private readonly actionMenuViewportMargin = 8;
  private readonly actionMenuItemHeight = 40;
  private readonly actionMenuPadding = 8;
  private readonly actionMenuBottomBarReserve = 88;
  private readonly rowMenuOptionsCache = new Map<string, DropdownOption[]>();
  private readonly destroyRef = inject(DestroyRef);
  private readonly isDummyService = inject(UnpaidPaymentsIsDummyService);
  private readonly accessService = inject(UnpaidPaymentsAccessService);
  private readonly hubOverviewService = inject(UnpaidPaymentsHubOverviewService);
  private lastRouteSignature = '';

  /** Urgent rows in the current list — same source as overview + Pay Urgent. */
  readonly urgentRowsForPay = computed(() =>
    this.payments().filter((p) => this.isRowPayable(p) && this.isUrgent(p))
  );
  readonly visibleUrgentRows = this.urgentRowsForPay;
  readonly moreOptions = computed<DropdownOption[]>(() => {
    const options: DropdownOption[] = [];
    const selected = this.selectedRows();
    const urgentCount = this.selectedUrgentCount();

    if (selected.length > 0 && urgentCount < selected.length) {
      options.push({
        value: 'mark_urgent',
        label: 'Mark Urgent',
        icon: 'zap',
        iconClass: 'text-[#ad46ff]',
      });
    }

    if (selected.length > 0 && urgentCount === selected.length && urgentCount > 0) {
      options.push({
        value: 'remove_urgent',
        label: 'Remove Urgent',
        icon: 'zap-off',
        iconClass: 'text-[#ad46ff]',
      });
    }

    return options;
  });
  readonly allVisibleSelected = computed(() => {
    const rows = this.payments();
    if (!rows.length) return false;
    return rows.every((p) => {
      const id = this.paymentId(p);
      return id !== null && this.selectedIds().includes(id);
    });
  });

  constructor(
    private readonly adapterService: UnpaidPaymentsAdapterService,
    private readonly productConfigService: ProductConfigService,
    private readonly partnerTileSummary: UnpaidPartnerTileSummaryService,
    private readonly unpaidPaymentsModal: UnpaidPaymentsModalService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) { }

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const nextState = parseFmListRouteState(params, this.fmRouteDefaults);
      const signature = JSON.stringify(nextState);
      if (signature === this.lastRouteSignature) {
        return;
      }
      this.lastRouteSignature = signature;
      this.applyRouteState(nextState);
      this.fetchPayments();
    });
  }

  private get fmRouteDefaults(): FmListRouteDefaults {
    return {
      filter: getDefaultFmFilter('recuvery'),
      supportedFilters: this.supportedFilters,
      includeDateRange: true,
      startDate: this.defaultStartDate,
      endDate: this.calculationService.unpaidPaymentsTodayYmd(),
      isDummy: getDefaultIsDummy(),
    };
  }

  onIsDummyToggle(isDummy: boolean): void {
    this.navigateWithFilters({ isDummy });
  }

  fetchPayments(): void {
    const product = this.adapterService.getProducts().find((p: UnifiedProductOption) => p.key === this.productKey);
    if (!product) return;
    const config = this.productConfigService.getProductConfig(product.serverName);
    if (!config?.productServerName) return;
    this.loading.set(true);
    this.closeActionMenu();
    this.rowMenuOptionsCache.clear();
    this.adapterService.listPayments(this.productKey, config.productServerName, this.buildListFilters()).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        if (!this.accessService.evaluateResponse(this.productKey, res)) {
          this.accessService.redirectToOverview(this.router, this.route);
          return;
        }
        this.payments.set(res?.payments || []);
        this.totalCount.set(Number(res?.count ?? res?.number_of_records ?? (res?.payments?.length || 0)));
        this.totalPages.set(this.calculationService.resolveApiPageCount(res, this.pageSize));
        this.totalAmount.set(Number(res?.total_amount ?? 0));
        const clampedPage = this.calculationService.clampPageToApiTotal(this.page(), this.totalPages());
        if (clampedPage != null) {
          this.navigateWithFilters({ page: clampedPage });
          return;
        }
        this.selectedIds.set([]);
        this.listAggregateIds.set(
          res?.contract_ids ??
          res?.papa_ids ??
          res?.depa_ids ??
          res?.missing_payment_ids ??
          res?.dispute_ids ??
          null
        );
        this.partnerTileSummary.updateFromListResponse(this.productKey, res);
        this.refreshPayableCount(this.payments());
        if (res?.available_source_balance) {
          const balanceMap = res.available_source_balance as Record<string, number>;
          this.availableSourceBalance.set(balanceMap);
          const sources = Object.entries(balanceMap).map(([key, val]) => ({
            label: `${key.replace(/_/g, ' ')} ($${Number(val).toFixed(2)})`,
            value: key,
          }));
          this.availableSources.set(sources);
        } else {
          this.availableSourceBalance.set({});
          this.availableSources.set([]);
        }
        this.syncUrgentOverviewFromList(res);
        this.fetchUrgentNotes();
        this.refreshFullListTotals(config.productServerName);
      },
      error: (err: unknown) => {
        this.loading.set(false);
        if (this.accessService.isAccessDenied(err)) {
          this.accessService.handleAccessDenied(this.productKey, this.router, this.route);
        }
        this.payments.set([]);
        this.totalCount.set(0);
        this.totalPages.set(0);
        this.totalAmount.set(0);
        this.listAggregateIds.set(null);
        this.payableCount.set(0);
        this.overviewCalculating.set(false);
        this.partnerTileSummary.updateFromListResponse(this.productKey, {});
        this.syncOverviewFromListTotals();
        this.syncUrgentOverviewFromList();
        this.fetchUrgentNotes();
      },
    });
  }

  private refreshFullListTotals(productServerName: string): void {
    const total = this.totalCount();
    if (total <= this.pageSize) {
      this.syncOverviewFromListTotals();
      return;
    }

    this.adapterService
      .listPayments(this.productKey, productServerName, { ...this.buildListFilters(), limit: total, offset: 0 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.totalAmount.set(Number(res?.total_amount ?? this.totalAmount()));
          this.listAggregateIds.set(
            res?.contract_ids ??
            res?.papa_ids ??
            res?.depa_ids ??
            res?.missing_payment_ids ??
            res?.dispute_ids ??
            this.listAggregateIds()
          );
          this.partnerTileSummary.updateFromListResponse(this.productKey, res);
          this.syncOverviewFromListTotals();
        },
        error: () => {
          this.syncOverviewFromListTotals();
        },
      });
  }

  paymentId(payment: UnifiedUnpaidPayment): string | number | null {
    return this.adapterService.getPaymentIdentifier(this.productKey, payment, { fm_filter: this.fmFilter() } as any);
  }

  rowId(payment: UnifiedUnpaidPayment): string {
    const id = this.paymentId(payment);
    return id === null ? '' : String(id);
  }

  get tableHeaders(): TableHeader[] {
    const headers: TableHeader[] = [
      { key: 'accountId', label: 'Account ID', sortable: false },
      { key: 'businessOwner', label: 'Business Owner Name', sortable: false },
      { key: 'businessOwnerEmail', label: 'Business Owner Email', sortable: false },
    ];

    if (this.showPaymentDateColumn()) {
      headers.push({ key: 'paymentDate', label: 'Payment Date', sortable: false });
    }
    if (this.showPartnerColumns()) {
      headers.push(
        { key: 'partnerName', label: 'Partner Name', sortable: false },
        { key: 'partnerEmail', label: 'Partner Email', sortable: false }
      );
    }
    if (this.showRecurringAmountColumn()) {
      headers.push({
        key: 'recurringAmount',
        label: 'Amount Charged',
        sortable: false,
      });
    }
    headers.push({
      key: 'transferAmount',
      label: 'Transfer To Business',
      sortable: false,
    });
    if (this.showBankAddedColumn()) {
      headers.push({ key: 'bankAdded', label: 'Bank Added', sortable: false });
    }
    headers.push({ key: 'action', label: 'Actions', sortable: false, align: 'text-left' });
    return headers;
  }

  canSelectRow(payment: UnifiedUnpaidPayment): boolean {
    return this.paymentId(payment) !== null;
  }

  isRowPayable(payment: UnifiedUnpaidPayment): boolean {
    if (this.paymentId(payment) === null) return false;
    if (this.calculationService.isRowTransferDisabled(payment)) return false;
    if (this.showBankAddedColumn()) {
      return this.getBankAdded(payment) !== 'No';
    }
    return true;
  }

  canPayRow(payment: UnifiedUnpaidPayment): boolean {
    if (!this.isRowPayable(payment)) return false;
    return this.selectedIds().length === 0;
  }

  isRowActionsDisabled(): boolean {
    return this.paying() || this.markingUrgent() || this.selectedIds().length > 0;
  }

  isUrgent(payment: UnifiedUnpaidPayment): boolean {
    return Number(payment['is_urgent']) === 1;
  }

  isVip(payment: UnifiedUnpaidPayment): boolean {
    return Number(payment['is_vip']) === 1;
  }

  hasUrgentNotes(payment: UnifiedUnpaidPayment): boolean {
    return this.getUrgentNotesForPayment(payment).length > 0;
  }

  getUrgentNotesForPayment(payment: UnifiedUnpaidPayment): any[] {
    const meta = this.getUrgentKeyMeta(payment);
    if (meta.value === undefined || meta.value === null || meta.value === '') return [];
    const paymentId = String(meta.value);
    return this.urgentNotes().filter(
      (note) => String(note['payment_id']) === paymentId && note['payment_key'] === meta.key
    );
  }

  onSelectionChange(selectedRows: UnifiedUnpaidPayment[]): void {
    const currentPageIds = this.payments()
      .map((p) => this.paymentId(p))
      .filter((id): id is number | string => id !== null);
    const selectedCurrentPageIds = selectedRows
      .map((p) => this.paymentId(p))
      .filter((id): id is number | string => id !== null);
    const otherPageIds = this.selectedIds().filter((id) => !currentPageIds.includes(id));
    this.selectedIds.set([...otherPageIds, ...selectedCurrentPageIds]);
    if (this.selectedIds().length > 0) {
      this.closeActionMenu();
    }
  }

  getRowMenuOptions(row: UnifiedUnpaidPayment): DropdownOption[] {
    const cacheKey = `${this.rowId(row)}:${this.isUrgent(row)}:${this.hasUrgentNotes(row)}`;
    if (this.rowMenuOptionsCache.has(cacheKey)) {
      return this.rowMenuOptionsCache.get(cacheKey)!;
    }

    const options: DropdownOption[] = [];
    if (!this.isUrgent(row)) {
      options.push({
        value: 'mark_urgent',
        label: 'Mark Urgent',
        icon: 'zap',
        iconClass: 'text-[#ad46ff]',
      });
    } else {
      options.push({
        value: 'remove_urgent',
        label: 'Remove Urgent',
        icon: 'zap-off',
        iconClass: 'text-[#ad46ff]',
      });
    }

    if (this.hasUrgentNotes(row)) {
      options.push({
        value: 'notes',
        label: 'Notes',
        icon: 'clipboard-list',
        iconClass: 'text-[#1581e5]',
      });
    }

    this.rowMenuOptionsCache.set(cacheKey, options);
    return options;
  }

  getDisplayRowMenuOptions(row: UnifiedUnpaidPayment): DropdownOption[] {
    return this.getRowMenuOptions(row);
  }

  onRowMenuOptionSelect(value: string | number, row: UnifiedUnpaidPayment, event: Event): void {
    event.stopPropagation();
    const raw = String(value);
    if (raw === 'mark_urgent') {
      this.markPaymentAsUrgent(row);
    } else if (raw === 'remove_urgent') {
      this.removePaymentFromUrgent(row);
    } else if (raw === 'notes') {
      this.showUrgentNotes(row);
    }
    this.closeActionMenu();
  }

  isActionMenuOpen(row: UnifiedUnpaidPayment): boolean {
    const id = this.rowId(row);
    return !!id && this.openActionMenuRowId() === id;
  }

  toggleActionMenu(row: UnifiedUnpaidPayment, event: Event): void {
    event.stopPropagation();
    if (this.isRowActionsDisabled()) return;
    const id = this.rowId(row);
    if (!id) return;
    if (this.openActionMenuRowId() === id) {
      this.closeActionMenu();
      return;
    }

    const trigger = event.currentTarget as HTMLElement;
    this.actionMenuTrigger = trigger;
    this.openActionMenuRowId.set(id);
    this.openActionMenuRow.set(row);
    this.actionMenuPosition.set(this.computeActionMenuPosition(trigger, row));
    setTimeout(() => this.refineActionMenuPosition(), 0);
  }

  closeActionMenu(): void {
    this.openActionMenuRowId.set(null);
    this.openActionMenuRow.set(null);
    this.actionMenuPosition.set(null);
    this.actionMenuTrigger = null;
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onActionMenuViewportChange(): void {
    const trigger = this.actionMenuTrigger;
    const row = this.openActionMenuRow();
    if (trigger && row && this.openActionMenuRowId()) {
      const measuredHeight =
        this.actionMenuPanel?.nativeElement.offsetHeight ?? this.estimateActionMenuHeight(row);
      this.actionMenuPosition.set(this.computeActionMenuPosition(trigger, row, measuredHeight));
    }
  }

  getAmount(payment: UnifiedUnpaidPayment): number {
    return Number(payment.pay_to_doctor || payment.pay_to_user || payment.amount || payment['pay_to_partner'] || 0);
  }

  getAccountCode(payment: UnifiedUnpaidPayment): string {
    return (
      payment['account_id'] ||
      payment['contract_code'] ||
      payment['payment_process_code'] ||
      String(this.paymentId(payment) || '-')
    );
  }

  getBusinessOwnerName(payment: UnifiedUnpaidPayment): string {
    return payment['doctor_name'] || payment['business_owner_name'] || payment['name'] || '-';
  }

  getBusinessOwnerEmail(payment: UnifiedUnpaidPayment): string {
    return payment['doctor_email'] || payment.email || '-';
  }

  getPaymentDate(payment: UnifiedUnpaidPayment): string {
    if (this.fmFilter() === 5) {
      return this.calculationService.formatUnpaidTableDate(payment['next_payment_date']);
    }
    return this.calculationService.formatUnpaidTableDate(payment['date_added'] || payment['last_contract_date']);
  }

  getAmountCharged(payment: UnifiedUnpaidPayment): number {
    return Number(payment['recurring_amount'] || payment['amount_deducted'] || payment.amount || 0);
  }

  getTransferToBusiness(payment: UnifiedUnpaidPayment): number {
    return Number(payment.pay_to_doctor || payment['pay_to_partner'] || payment.pay_to_user || 0);
  }

  getBankAdded(payment: UnifiedUnpaidPayment): string {
    return payment['bank_added'] || '-';
  }

  showPartnerColumns(): boolean {
    return (this.fmFilter() === 4 && this.fmStatusFilter() === '3') || (this.fmFilter() === 9 && this.fmStatusFilter() === '1');
  }

  showRecurringAmountColumn(): boolean {
    return this.fmFilter() !== 9 && this.fmFilter() !== 10 && this.fmFilter() !== 12;
  }

  showBankAddedColumn(): boolean {
    return this.fmFilter() === 5 || this.fmFilter() === 8 || this.fmFilter() === 10 || this.fmFilter() === 11;
  }

  showPaymentDateColumn(): boolean {
    return (
      this.fmFilter() === 5 ||
      this.fmFilter() === 10 ||
      this.fmFilter() === 11 ||
      this.fmFilter() === 12 ||
      this.fmFilter() === 13
    );
  }

  getPartnerName(payment: UnifiedUnpaidPayment): string {
    return `${payment['partner_title'] ? payment['partner_title'] + ' ' : ''}${payment['partner_name'] || '-'}`;
  }

  getPartnerEmail(payment: UnifiedUnpaidPayment): string {
    return payment['partner_email'] || '-';
  }

  getRecurringAmount(payment: UnifiedUnpaidPayment): number {
    return Number(payment['recurring_amount'] || 0);
  }

  getPayableAmount(payment: UnifiedUnpaidPayment): number {
    if (this.fmFilter() === 4 && this.fmStatusFilter() !== '3') {
      return Number(payment['amount_deducted'] || 0);
    }
    if (this.fmFilter() === 9 && this.fmStatusFilter() === '1') {
      return Number(payment['pay_to_partner'] || 0);
    }
    return Number(payment['pay_to_doctor'] || 0);
  }

  markPaymentAsUrgent(payment: UnifiedUnpaidPayment): void {
    this.unpaidPaymentsModal
      .openUrgentRequiredNote(true)
      .subscribe((result) => {
        if (!result?.confirmed) return;
        this.updateUrgentState(payment, result.note, true);
      });
  }

  removePaymentFromUrgent(payment: UnifiedUnpaidPayment): void {
    this.unpaidPaymentsModal
      .openUrgentRequiredNote(false)
      .subscribe((result) => {
        if (!result?.confirmed) return;
        this.updateUrgentState(payment, result.note, false);
      });
  }

  showUrgentNotes(payment: UnifiedUnpaidPayment): void {
    this.unpaidPaymentsModal.openNotes({
      title: 'Notes',
      notes: this.getUrgentNotesForPayment(payment),
      emptyMessage: 'No urgent notes found for this payment.',
    });
  }

  paySingle(payment: UnifiedUnpaidPayment): void {
    if (!this.canPayRow(payment)) return;
    this.confirmPayRows([payment]);
  }

  paySelected(): void {
    const picked = this.selectedPayablePayments();
    if (!picked.length) return;
    this.confirmPayRows(picked);
  }

  payAllVisible(): void {
    const rows = this.payments().filter((p) => this.isRowPayable(p));
    if (!rows.length) return;
    this.confirmPayRows(rows);
  }

  payAllToBusiness(): void {
    const count = this.payableCount();
    if (!count) return;
    const total = this.totalAmount() || this.overview().totalPaymentToPay;
    this.confirmPayAll(total, count);
  }

  payAllUrgentVisible(): void {
    const rows = this.urgentRowsForPay();
    if (!rows.length) return;
    this.confirmPayRows(rows);
  }

  toggleSelectAllVisible(): void {
    if (this.allVisibleSelected()) {
      this.selectedIds.set([]);
      return;
    }
    const ids = this.payments()
      .map((p) => this.paymentId(p))
      .filter((id): id is number | string => id !== null);
    this.selectedIds.set(ids);
  }

  applyFilters(): void {
    this.onSearch();
  }

  clearSearch(): void {
    this.navigateWithFilters({ search: '', page: 1 });
  }

  onMoreOptionClick(value: unknown): void {
    const raw = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
    if (raw === 'mark_urgent') {
      this.markSelectedUrgent(true);
    } else if (raw === 'remove_urgent') {
      this.markSelectedUrgent(false);
    }
  }

  markSelectedUrgent(makeUrgent: boolean): void {
    const rows = this.selectedRows();
    if (!rows.length) return;
    this.unpaidPaymentsModal
      .openUrgentRequiredNote(makeUrgent)
      .subscribe((result) => {
        if (!result?.confirmed) return;
        for (const row of rows) {
          this.updateUrgentState(row, result.note, makeUrgent);
        }
      });
  }

  onStartDateChange(value: string): void {
    const clamped = this.clampDateRange(value, this.endDate());
    if (clamped.startDate === this.startDate()) return;
    this.navigateWithFilters({ startDate: clamped.startDate, endDate: clamped.endDate, page: 1 });
  }

  onEndDateChange(value: string): void {
    const clamped = this.clampDateRange(this.startDate(), value);
    if (clamped.endDate === this.endDate() && clamped.startDate === this.startDate()) return;
    this.navigateWithFilters({ startDate: clamped.startDate, endDate: clamped.endDate, page: 1 });
  }

  resetFilters(): void {
    this.navigateWithFilters({
      search: '',
      startDate: this.defaultStartDate,
      endDate: this.calculationService.unpaidPaymentsTodayYmd(),
      status: '0',
      vip: 0,
      urgent: false,
      page: 1,
    });
  }

  setUrgentOnly(checked: boolean): void {
    if (this.urgentOnly() === checked) return;
    this.navigateWithFilters({ urgent: checked, page: 1 });
  }

  onFilterDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.onFilterChange(Number(raw));
  }

  onVipDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === null) return;
    this.navigateWithFilters({ vip: Number(raw), page: 1 });
  }

  onExportDropdownChange(value: unknown): void {
    const raw = this.coerceDropdownPrimitive(value);
    if (raw === 'xlsx') {
      this.exportRows(false);
    } else if (raw === 'csv') {
      this.exportRows(true);
    }
  }

  exportRows(asCsv = false): void {
    const product = this.adapterService.getProducts().find((p: UnifiedProductOption) => p.key === this.productKey);
    if (!product) return;
    const config = this.productConfigService.getProductConfig(product.serverName);
    if (!config?.productServerName) return;

    this.exporting.set(true);
    this.adapterService
      .listPayments(this.productKey, config.productServerName, {
        ...this.buildListFilters(),
        limit: this.totalCount() || this.pageSize,
        offset: 0,
      })
      .subscribe({
        next: (res: any) => {
          this.exporting.set(false);
          const rows: UnifiedUnpaidPayment[] = Array.isArray(res?.payments) ? res.payments : [];
          const exportRows = rows.map((payment: UnifiedUnpaidPayment) => ({
            'Account ID': this.getAccountCode(payment),
            'Business Owner Name': this.getBusinessOwnerName(payment),
            'Business Owner Email': this.getBusinessOwnerEmail(payment),
            'Payment Date': this.showPaymentDateColumn() ? this.getPaymentDate(payment) : '',
            'Partner Name': this.showPartnerColumns() ? this.getPartnerName(payment) : '',
            'Partner Email': this.showPartnerColumns() ? this.getPartnerEmail(payment) : '',
            'Amount Charged': this.showRecurringAmountColumn() ? this.getAmountCharged(payment) : '',
            'Transfer To Business': this.getTransferToBusiness(payment),
            'Payable Amount': this.getPayableAmount(payment),
            'Bank Added': this.showBankAddedColumn() ? this.getBankAdded(payment) : '',
            VIP: this.isVip(payment) ? 'Yes' : 'No',
            Urgent: this.isUrgent(payment) ? 'Yes' : 'No',
          }));
          const sheet = XLSX.utils.json_to_sheet(exportRows);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, sheet, 'Recuvery Unpaid');
          XLSX.writeFile(workbook, asCsv ? 'recuvery_unpaid_payments.csv' : 'recuvery_unpaid_payments.xlsx');
        },
        error: () => {
          this.exporting.set(false);
          this.openActionError('Recuvery export could not be generated. Please try again.', 'Export Failed');
        },
      });
  }

  onFilterChange(next: number): void {
    if (!this.supportedFilters.includes(next as any)) {
      return;
    }
    this.navigateWithFilters({ filter: next, page: 1 });
  }

  onSearch(): void {
    this.navigateWithFilters({
      search: this.search(),
      startDate: this.startDate(),
      endDate: this.endDate(),
      page: 1,
    });
  }

  goToPage(next: number): void {
    if (next < 1) return;
    if (next > this.totalPages()) return;
    this.navigateWithFilters({ page: next });
  }

  private confirmPayAll(total: number, paymentCount: number): void {
    const openPaymentConfirm = (transferSource?: UnpaidPaymentsFundingSourceId): void => {
      this.unpaidPaymentsModal
        .openConfirmPayment({
          title: this.paymentConfirmTitle,
          totalAmount: total,
          paymentCount,
          confirmLabel: 'Pay',
          cancelLabel: 'Cancel',
        })
        .subscribe((result) => {
          if (!result?.confirmed) return;
          this.payAll(total, transferSource);
        });
    };

    if (this.shouldUseTransferFromModal()) {
      if (!this.buildTransferSourceOptions().length) {
        this.openPaymentError('No funding source is available for this payout.');
        return;
      }
      this.openTransferModal(total, (source) => openPaymentConfirm(source));
      return;
    }

    openPaymentConfirm();
  }

  private confirmPayRows(rows: UnifiedUnpaidPayment[]): void {
    const total = rows.reduce((sum, row) => sum + this.getPayableAmount(row), 0);

    const openPaymentConfirm = (transferSource?: UnpaidPaymentsFundingSourceId): void => {
      this.unpaidPaymentsModal
        .openConfirmPayment({
          title: this.paymentConfirmTitle,
          totalAmount: total,
          paymentCount: rows.length,
          confirmLabel: 'Pay',
          cancelLabel: 'Cancel',
        })
        .subscribe((result) => {
          if (!result?.confirmed) return;
          this.payByRows(rows, total, transferSource);
        });
    };

    if (this.shouldUseTransferFromModal()) {
      if (!this.buildTransferSourceOptions().length) {
        this.openPaymentError('No funding source is available for this payout.');
        return;
      }
      this.openTransferModal(total, (source) => openPaymentConfirm(source));
      return;
    }

    openPaymentConfirm();
  }

  private openTransferModal(total: number, onConfirm: (source: UnpaidPaymentsFundingSourceId) => void): void {
    const sources = this.buildTransferSourceOptions();
    if (!sources.length) {
      this.openPaymentError('No funding source is available for this payout.');
      return;
    }

    this.unpaidPaymentsModal
      .openTransferFrom({
        totalAmount: total,
        sources,
        title: 'Transfer From',
        confirmLabel: 'Continue',
      })
      .subscribe((result) => {
        if (!result?.confirmed) return;

        const available = sources.find((source) => source.id === result.source)?.availableAmount ?? 0;
        if (available < total) {
          this.openPaymentError(
            `Insufficient ${result.source === 'card' ? 'card' : 'bank'} balance. Available: $${available.toFixed(2)}.`
          );
          return;
        }

        onConfirm(result.source);
      });
  }

  private payByRows(
    rows: UnifiedUnpaidPayment[],
    paidAmount: number,
    transferSource?: UnpaidPaymentsFundingSourceId
  ): void {
    if (!rows.length) return;
    const product = this.adapterService.getProducts().find((p: UnifiedProductOption) => p.key === this.productKey);
    if (!product) return;
    const config = this.productConfigService.getProductConfig(product.serverName);
    if (!config?.productServerName) return;

    this.paying.set(true);
    this.adapterService
      .releasePayments(this.productKey, config.productServerName, rows, {
        ...this.buildListFilters(),
        transfer_from: this.normalizeTransferSource(transferSource),
      })
      .subscribe({
        next: (res: any) => {
          this.paying.set(false);
          const errors = Array.isArray(res?.error_tracking) ? res.error_tracking : [];
          if (errors.length) {
            this.showErrorTracking(errors, 'Payment completed with issues');
            this.selectedIds.set([]);
            this.fetchPayments();
            return;
          }
          if (res?.is_error === 0 || res?.is_error == null) {
            this.selectedIds.set([]);
            this.unpaidPaymentsModal
              .openPaymentStatus({
                amount: paidAmount,
                statusMessage: 'Paid Successfully',
                confirmLabel: 'Done',
              })
              .subscribe(() => {
                this.fetchPayments();
              });
            return;
          }
          this.openPaymentError(
            res?.original_text || res?.message || 'Unable to complete the payment.',
            'Payment Failed'
          );
        },
        error: () => {
          this.paying.set(false);
          this.openPaymentError('Unable to complete the payment.', 'Payment Failed');
        },
      });
  }

  private payAll(paidAmount: number, transferSource?: UnpaidPaymentsFundingSourceId): void {
    const product = this.adapterService.getProducts().find((p: UnifiedProductOption) => p.key === this.productKey);
    if (!product) return;
    const config = this.productConfigService.getProductConfig(product.serverName);
    if (!config?.productServerName) return;

    this.paying.set(true);
    this.adapterService
      .releaseAllPayments(this.productKey, config.productServerName, {
        ...this.buildListFilters(),
        aggregate_ids: this.listAggregateIds(),
        transfer_from: this.normalizeTransferSource(transferSource),
      })
      .subscribe({
        next: (res: any) => {
          this.paying.set(false);
          const errors = Array.isArray(res?.error_tracking) ? res.error_tracking : [];
          if (errors.length) {
            this.showErrorTracking(errors, 'Payment completed with issues');
            this.selectedIds.set([]);
            this.fetchPayments();
            return;
          }
          if (res?.is_error === 0 || res?.is_error == null) {
            this.selectedIds.set([]);
            this.unpaidPaymentsModal
              .openPaymentStatus({
                amount: paidAmount,
                statusMessage: 'Paid Successfully',
                confirmLabel: 'Done',
              })
              .subscribe(() => {
                this.fetchPayments();
              });
            return;
          }
          this.openPaymentError(
            res?.original_text || res?.message || 'Unable to complete the payment.',
            'Payment Failed'
          );
        },
        error: () => {
          this.paying.set(false);
          this.openPaymentError('Unable to complete the payment.', 'Payment Failed');
        },
      });
  }

  private updateUrgentState(payment: UnifiedUnpaidPayment, note: string, makeUrgent: boolean): void {
    const product = this.adapterService.getProducts().find((p: UnifiedProductOption) => p.key === this.productKey);
    if (!product) return;
    const config = this.productConfigService.getProductConfig(product.serverName);
    if (!config?.productServerName) return;

    const meta = this.getUrgentKeyMeta(payment);
    if (!meta.value) {
      this.openActionError(
        'This payment does not expose the required identifier.',
        'Urgent Action Unavailable'
      );
      return;
    }

    const actionLabel = makeUrgent ? 'mark payment as urgent' : 'remove payment from urgent';
    this.markingUrgent.set(true);
    this.adapterService
      .request(
        this.productKey,
        config.productServerName,
        'post',
        this.adapterService.resolveProductApiPath(
          this.productKey,
          `finance/${makeUrgent ? 'mark_payment_urgent' : 'mark_payment_non_urgent'}`
        ),
        {
          primary_key_name: meta.key,
          payment_id: meta.value,
          filter: String(this.fmFilter()),
          primary_key_name2: meta.key,
          is_urgent: makeUrgent ? '1' : '0',
          admin_notes: note,
          country_id: this.adapterService.getProductCountryId(this.productKey),
        },
        this.buildListFilters()
      )
      .subscribe({
        next: (res: any) => {
          this.markingUrgent.set(false);
          if (res?.is_error) {
            this.openActionError(res?.message || res?.original_text || `Unable to ${actionLabel}.`, 'Urgent Action Failed');
            return;
          }
          const nextRows = [...this.payments()];
          const idx = nextRows.findIndex((row) => this.paymentId(row) === this.paymentId(payment));
          if (idx > -1) {
            nextRows[idx] = { ...nextRows[idx], is_urgent: makeUrgent ? 1 : 0 };
          }
          this.rowMenuOptionsCache.clear();
          this.payments.set(nextRows);
          this.syncUrgentOverviewFromList();
          this.fetchUrgentNotes();
        },
        error: () => {
          this.markingUrgent.set(false);
          this.openActionError(`Unable to ${actionLabel}.`, 'Urgent Action Failed');
        },
      });
  }

  private fetchUrgentNotes(): void {
    const product = this.adapterService.getProducts().find((p: UnifiedProductOption) => p.key === this.productKey);
    if (!product) return;
    const config = this.productConfigService.getProductConfig(product.serverName);
    if (!config?.productServerName) return;

    this.adapterService
      .request<any>(
        this.productKey,
        config.productServerName,
        'get',
        this.adapterService.resolveProductApiPath(this.productKey, 'finance/fetch_unpaid_urgent_notes'),
        {
          country_id: this.adapterService.getProductCountryId(this.productKey),
          show_toaster_messages: false,
        }
      )
      .subscribe({
        next: (res) => {
          this.urgentNotes.set(res?.data?.notes_list || []);
          this.rowMenuOptionsCache.clear();
        },
        error: () => {
          this.urgentNotes.set([]);
          this.rowMenuOptionsCache.clear();
        },
      });
  }

  /** Urgent overview + Pay Urgent use list rows (`is_urgent`), not Credee's global urgent queue API. */
  private syncUrgentOverviewFromList(res?: { payments?: UnifiedUnpaidPayment[] } & Record<string, unknown>): void {
    const rootCount = Number(
      res?.['total_urgent_payments'] ?? res?.['urgent_count'] ?? res?.['urgent_payments_count'] ?? NaN
    );
    const rootAmount = Number(
      res?.['urgent_balance'] ?? res?.['urgent_amount'] ?? res?.['total_urgent_amount'] ?? NaN
    );
    const hasRootTotals =
      (Number.isFinite(rootCount) && rootCount > 0) || (Number.isFinite(rootAmount) && rootAmount > 0);

    const fromRows = this.computeUrgentTotalsFromPayments(
      (res?.payments ?? this.payments()) as UnifiedUnpaidPayment[]
    );

    this.partnerTileSummary.patchOverview(this.productKey, {
      urgentPaymentsCount: hasRootTotals && Number.isFinite(rootCount) ? rootCount : fromRows.count,
      urgentPaymentsAmount: hasRootTotals && Number.isFinite(rootAmount) ? rootAmount : fromRows.amount,
    });
  }

  private computeUrgentTotalsFromPayments(payments: UnifiedUnpaidPayment[]): {
    count: number;
    amount: number;
  } {
    const urgentRows = payments.filter((p) => this.isUrgent(p));
    return {
      count: urgentRows.length,
      amount: urgentRows.reduce((sum, p) => sum + this.getPayableAmount(p), 0),
    };
  }

  private openActionError(statusMessage: string, title = 'Action Failed', subtitle?: string): void {
    this.unpaidPaymentsModal.openActionError({ title, statusMessage, subtitle }).subscribe();
  }

  private getUrgentKeyMeta(payment: UnifiedUnpaidPayment): { key: string; value: any } {
    switch (this.fmFilter()) {
      case 4:
        return { key: 'disp_id', value: payment['disp_id'] ?? payment['dispute_id'] };
      case 5:
      case 8:
        return { key: 'dopa_id', value: payment['dopa_id'] };
      case 9:
        return this.fmStatusFilter() === '1'
          ? { key: 'papa_id', value: payment['papa_id'] }
          : { key: 'dopa_id', value: payment['dopa_id'] };
      case 10:
      case 11:
      case 12:
      case 13:
        return this.fmStatusFilter() === '1'
          ? { key: 'papa_id', value: payment['papa_id'] }
          : { key: 'dopa_id', value: payment['dopa_id'] };
      default:
        return { key: 'dopa_id', value: payment['dopa_id'] };
    }
  }

  private buildListFilters() {
    return {
      search_string: this.search(),
      limit: this.pageSize,
      offset: this.offset(),
      start_date: this.startDate(),
      end_date: this.endDate(),
      fm_filter: this.fmFilter(),
      fm_status_filter: this.fmStatusFilter(),
      is_vip: this.vipType(),
      is_urgent: this.urgentOnly() ? '1' : '0',
    };
  }

  private coerceDropdownPrimitive(value: unknown): string | number | null {
    if (value === undefined || value === null) return null;
    const single = Array.isArray(value) ? value[0] : value;
    if (single === undefined || single === null) return null;
    return single as string | number;
  }

  private estimateActionMenuHeight(row: UnifiedUnpaidPayment): number {
    return this.getDisplayRowMenuOptions(row).length * this.actionMenuItemHeight + this.actionMenuPadding;
  }

  private computeActionMenuPosition(
    trigger: HTMLElement,
    row: UnifiedUnpaidPayment,
    menuHeight = this.estimateActionMenuHeight(row)
  ): { top: number; left: number } {
    const rect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const bottomReserve = this.selectedIds().length > 0 ? this.actionMenuBottomBarReserve : 0;
    const maxBottom = viewportHeight - bottomReserve - this.actionMenuViewportMargin;
    const maxTop = this.actionMenuViewportMargin;

    const spaceBelow = maxBottom - rect.bottom;
    const spaceAbove = rect.top - maxTop;

    let top =
      spaceBelow >= menuHeight + this.actionMenuGap || spaceBelow >= spaceAbove
        ? rect.bottom + this.actionMenuGap
        : rect.top - menuHeight - this.actionMenuGap;

    top = Math.max(maxTop, Math.min(top, maxBottom - menuHeight));

    let left = rect.right - this.actionMenuWidth;
    left = Math.max(
      this.actionMenuViewportMargin,
      Math.min(left, viewportWidth - this.actionMenuWidth - this.actionMenuViewportMargin)
    );

    return { top, left };
  }

  private refineActionMenuPosition(): void {
    const trigger = this.actionMenuTrigger;
    const row = this.openActionMenuRow();
    if (!trigger || !row || !this.openActionMenuRowId()) return;

    const measuredHeight = this.actionMenuPanel?.nativeElement.offsetHeight;
    if (!measuredHeight) return;

    this.actionMenuPosition.set(this.computeActionMenuPosition(trigger, row, measuredHeight));
  }

  private requiresFundingSource(): boolean {
    return (this.fmFilter() === 5 || this.fmFilter() === 8) && this.availableSources().length > 0;
  }

  private shouldUseTransferFromModal(): boolean {
    return this.fmFilter() === 5 || this.fmFilter() === 8;
  }

  /** Amount To Pay overview uses full filtered listing totals, not the current table page. */
  private syncOverviewFromListTotals(): void {
    this.partnerTileSummary.patchOverview(this.productKey, {
      totalPaymentsCount: this.payableCount(),
      totalPaymentToPay: this.totalAmount(),
    });
    this.hubOverviewService.cacheProductOverviewFromPage(this.productKey);
  }

  private refreshPayableCount(pageRows: UnifiedUnpaidPayment[]): void {
    const product = this.adapterService.getProducts().find((p: UnifiedProductOption) => p.key === this.productKey);
    if (!product) {
      this.overviewCalculating.set(false);
      return;
    }
    const config = this.productConfigService.getProductConfig(product.serverName);
    if (!config?.productServerName) {
      this.overviewCalculating.set(false);
      return;
    }

    const requestId = ++this.payableCountRequestId;
    const total = this.totalCount();
    this.overviewCalculating.set(true);

    this.calculationService.resolvePayableCountFromListing(
      (limit, offset) =>
        this.adapterService.listPayments(this.productKey, config.productServerName, {
          ...this.buildListFilters(),
          limit,
          offset,
        }),
      total,
      pageRows,
      (payment) => this.isRowPayable(payment)
    ).subscribe({
      next: (count) => {
        if (requestId !== this.payableCountRequestId) return;
        this.payableCount.set(count);
        this.syncOverviewFromListTotals();
        this.overviewCalculating.set(false);
      },
      error: () => {
        if (requestId !== this.payableCountRequestId) return;
        this.payableCount.set(0);
        this.syncOverviewFromListTotals();
        this.overviewCalculating.set(false);
      },
    });
  }

  private buildTransferSourceOptions(): UnpaidPaymentsFundingSourceOption[] {
    const balanceMap = this.availableSourceBalance();
    return Object.entries(balanceMap)
      .map(([key, amount]) => {
        const id = this.toFundingSourceId(key);
        if (!id) return null;
        return {
          id,
          label: id === 'card' ? 'Card' : 'Bank',
          availableAmount: Number(amount) || 0,
        } satisfies UnpaidPaymentsFundingSourceOption;
      })
      .filter((source): source is UnpaidPaymentsFundingSourceOption => source !== null);
  }

  private toFundingSourceId(key: string): UnpaidPaymentsFundingSourceId | null {
    const normalized = key.toLowerCase();
    if (normalized.includes('bank')) return 'bank_account';
    if (normalized.includes('card')) return 'card';
    return null;
  }

  private normalizeTransferSource(
    transferSource?: UnpaidPaymentsFundingSourceId
  ): 'BANK' | 'CARD' | undefined {
    if (!transferSource) return undefined;
    return transferSource === 'bank_account' ? 'BANK' : 'CARD';
  }

  private showErrorTracking(errors: any[], title: string): void {
    const subtitle = errors
      .map((error, index) => `${index + 1}. ${error?.error_message || error?.message || JSON.stringify(error)}`)
      .join('\n');
    this.unpaidPaymentsModal.openPaymentError({
      title: 'Payment Status',
      statusMessage: title,
      subtitle: subtitle || 'One or more records failed to process.',
      confirmLabel: 'Done',
    });
  }

  private openPaymentError(statusMessage: string, title = 'Payment Status'): void {
    this.unpaidPaymentsModal.openPaymentError({
      title,
      statusMessage,
      confirmLabel: 'Done',
    });
  }

  private readRouteStateFromSignals(): FmListRouteState {
    const fromRoute = parseFmListRouteState(this.route.snapshot.queryParamMap, this.fmRouteDefaults);
    return {
      ...fromRoute,
      filter: this.fmFilter(),
      status: this.fmStatusFilter(),
      vip: this.vipType(),
      urgent: this.urgentOnly(),
      search: this.search(),
      page: this.page(),
      startDate: this.startDate(),
      endDate: this.endDate(),
      isDummy: this.isDummyService.isDummy(),
    };
  }

  private clampDateRange(start: string, end: string): { startDate: string; endDate: string } {
    return this.calculationService.clampUnpaidListDateRange(start, end);
  }

  private applyRouteState(state: FmListRouteState): void {
    this.isDummyService.syncFromRoute(state.isDummy);
    this.fmFilter.set(state.filter);
    this.fmStatusFilter.set(state.status);
    this.vipType.set(state.vip);
    this.urgentOnly.set(state.urgent);
    this.search.set(state.search);
    this.searchApplied.set(!!state.search.trim());
    this.page.set(state.page);
    const clamped = this.clampDateRange(
      state.startDate || this.defaultStartDate,
      state.endDate || this.calculationService.unpaidPaymentsTodayYmd()
    );
    this.startDate.set(clamped.startDate);
    this.endDate.set(clamped.endDate);
    this.selectedIds.set([]);
    this.rowMenuOptionsCache.clear();
  }

  private navigateWithFilters(patch: Partial<FmListRouteState>): void {
    const next: FmListRouteState = { ...this.readRouteStateFromSignals(), ...patch };
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: buildFmListQueryParams(next, this.fmRouteDefaults),
      replaceUrl: true,
    });
  }

}
